import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, AlertTriangle, GitBranch, Rocket } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CHIP = {
  ok: { label: 'Deployed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  warn: { label: 'Deployed with warnings', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const Versions = ({ v = {} }) => (
  <p className="text-xs text-gray-600" data-testid="last-deploy-versions">
    Source v{v.source || '?'} · Build v{v.build || '?'} · Live v{v.live_after || v.live_before || '?'} · Backend v{v.backend || '?'}
  </p>
);

const StepRow = ({ s, i }) => (
  <li className="flex items-start gap-2 text-sm" data-testid={`last-deploy-step-${i}`}>
    {s.ok
      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
      : <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
    <span className={s.ok ? 'text-gray-800' : 'text-red-700 font-medium'}>{s.detail || s.name}</span>
  </li>
);

export const LastDeployCard = ({ refreshKey }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/settings/last-deploy`)
      .then((r) => setData(r.data))
      .catch(() => setData({ found: false }));
  }, [refreshKey]);

  if (!data) return null;
  const state = data.result === 'failed' ? 'failed' : (data.warnings?.length ? 'warn' : 'ok');
  const chip = CHIP[state];

  return (
    <Card data-testid="last-deploy-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Rocket className="h-4 w-4 text-gray-500" /> Last deploy
          {data.found && <Badge className={`${chip.cls} text-[10px]`} data-testid="last-deploy-status">{chip.label}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data.found ? (
          <p className="text-sm text-gray-500" data-testid="last-deploy-empty">
            No deploy recorded on this server yet. After the next Plesk “Deploy now”, every step shows up here.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap" data-testid="last-deploy-meta">
              <GitBranch className="h-3.5 w-3.5" />
              <span>{data.branch || 'branch unknown'}</span>
              {data.commit && <span className="font-mono">· {data.commit}</span>}
              <span>· {data.finished_at ? new Date(data.finished_at).toLocaleString() : '—'}</span>
              {data.duration_s != null && <span>· {data.duration_s}s</span>}
            </div>
            <Versions v={data.versions} />
            <ul className="space-y-1.5" data-testid="last-deploy-steps">
              {(data.steps || []).map((s, i) => <StepRow key={`${s.name}-${i}`} s={s} i={i} />)}
            </ul>
            {(data.warnings || []).map((w) => (
              <p key={w} className="flex items-start gap-2 text-xs text-amber-700" data-testid="last-deploy-warning">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {w}
              </p>
            ))}
            {data.result === 'failed' && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5" data-testid="last-deploy-reason">
                Failed at “{data.failed_step}”: {data.reason}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
